import { ReactNode, useState } from 'react'
import { isSameDay } from 'date-fns'
import { Box } from '@factorialco/deprecated-design-system'
import { F0Text } from '@factorialco/f0-react'
import i18n from 'modules/core/lib/i18n'
import type {
  TimeGranularity,
  TimeSlot,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'

interface Props {
  rowHeaderLabel?: ReactNode
  rowHeaderWidth: number
  minColumnWidth: number
  gridMinWidth: number
  showGridLines: boolean
  stickyHeaders: boolean
  granularity: TimeGranularity
  slots: TimeSlot[]
  timeSlotFormatter: (slot: TimeSlot) => ReactNode
  selectedColumnIndex?: number | null
  isAllSelected?: boolean
  onColumnHeaderSelected?: (slotIndex: number) => void
  onCornerCellSelected?: () => void
}

const TimeGridHeader = ({
  rowHeaderLabel,
  rowHeaderWidth,
  minColumnWidth,
  gridMinWidth,
  showGridLines,
  stickyHeaders,
  granularity,
  slots,
  timeSlotFormatter,
  selectedColumnIndex,
  isAllSelected,
  onColumnHeaderSelected,
  onCornerCellSelected,
}: Props) => {
  const [hoveredHeaderIndex, setHoveredHeaderIndex] = useState<number | 'corner' | null>(null)

  const cornerCellStyle: Partial<CSSStyleDeclaration> = {
    position: 'relative',
    paddingTop: '4px',
    paddingRight: '4px',
    paddingBottom: '6px',
    paddingLeft: '16px',
    minHeight: '36px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    userSelect: 'none',
  }

  const slotCellStyle: Partial<CSSStyleDeclaration> = {
    position: 'relative',
    paddingTop: '4px',
    paddingRight: '8px',
    paddingBottom: '6px',
    paddingLeft: '8px',
    minHeight: '36px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    userSelect: 'none',
    textAlign: 'center',
  }

  const renderSlotLabel = (slot: TimeSlot, formattedSlot: ReactNode) => {
    if (typeof formattedSlot !== 'string' && typeof formattedSlot !== 'number') {
      return formattedSlot
    }

    const label = String(formattedSlot).trim()
    const dayNumber = label.match(/\d+/)?.[0] ?? ''
    const weekday = label.replace(/\d+/g, '').trim()
    const isTodaySlot = isSameDay(slot.start, new Date())

    if (isTodaySlot && dayNumber && weekday) {
      return (
        <Box flexDirection="row" alignItems="center" gap="s4" style={{ userSelect: 'none' }}>
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            className="bg-f1-icon-accent"
            borderRadius={{ all: 'abs004' }}
            height="s20"
            width="s24"
            style={{ userSelect: 'none' }}
          >
            <F0Text variant="inverse" content={dayNumber} />
          </Box>
          <F0Text content={weekday} variant="description" />
        </Box>
      )
    }

    if (dayNumber && weekday) {
      return (
        <Box
          flexDirection="row"
          alignItems="center"
          gap="s2"
          style={{ userSelect: 'none', fontWeight: '500' }}
        >
          <F0Text content={dayNumber} variant="description" />
          {granularity !== 'week' ? (
            <F0Text
              content={weekday}
              variant="description"
              style={{ color: 'hsl(var(--neutral-40))' }}
            />
          ) : (
            <F0Text content={weekday} variant="description" />
          )}
        </Box>
      )
    }

    return <F0Text content={label} />
  }

  return (
    <Box
      role="row"
      data-testid="time-grid-header"
      data-grid-width={gridMinWidth}
      background="grey000White"
      style={{
        display: 'grid',
        gridTemplateColumns: `${rowHeaderWidth}px repeat(${slots.length}, minmax(${minColumnWidth}px, 1fr))`,
        minWidth: String(gridMinWidth),
        position: stickyHeaders ? 'sticky' : 'relative',
        top: '0px',
        zIndex: stickyHeaders ? '3' : '1',
      }}
    >
      <Box
        role="columnheader"
        onClick={onCornerCellSelected}
        onMouseEnter={() => setHoveredHeaderIndex('corner')}
        onMouseLeave={() => setHoveredHeaderIndex(null)}
        aria-label={onCornerCellSelected ? i18n.t('common.select_all') : undefined}
        aria-pressed={onCornerCellSelected ? isAllSelected : undefined}
        border={
          showGridLines ? { bottom: { width: 's1', style: 'solid', color: 'grey300' } } : undefined
        }
        style={{
          ...cornerCellStyle,
          cursor: onCornerCellSelected ? 'pointer' : undefined,
          borderBottomWidth: hoveredHeaderIndex === 'corner' || isAllSelected ? '0' : undefined,
        }}
      >
        {typeof rowHeaderLabel === 'string' || typeof rowHeaderLabel === 'number' ? (
          <F0Text content={String(rowHeaderLabel)} />
        ) : (
          rowHeaderLabel
        )}
        {showGridLines && (hoveredHeaderIndex === 'corner' || isAllSelected) ? (
          <Box
            aria-hidden="true"
            className="bg-f1-icon-positive"
            style={{
              position: 'absolute',
              bottom: '0',
              left: '0',
              right: '0',
              height: '2px',
              opacity: isAllSelected ? '0.7' : '0.3',
              pointerEvents: 'none',
            }}
          />
        ) : null}
      </Box>
      {slots.map((slot) => {
        const isColumnSelected = isAllSelected || selectedColumnIndex === slot.index
        const formattedSlot = timeSlotFormatter(slot)
        return (
          <Box
            key={slot.index}
            role="columnheader"
            onClick={onColumnHeaderSelected ? () => onColumnHeaderSelected(slot.index) : undefined}
            onMouseEnter={() => setHoveredHeaderIndex(slot.index)}
            onMouseLeave={() => setHoveredHeaderIndex(null)}
            border={
              showGridLines
                ? { bottom: { width: 's1', style: 'solid', color: 'grey300' } }
                : undefined
            }
            style={{
              ...slotCellStyle,
              cursor: onColumnHeaderSelected ? 'pointer' : undefined,
              borderBottomWidth:
                hoveredHeaderIndex === slot.index || isColumnSelected ? '0' : undefined,
            }}
          >
            {renderSlotLabel(slot, formattedSlot)}
            {showGridLines && (hoveredHeaderIndex === slot.index || isColumnSelected) ? (
              <Box
                aria-hidden="true"
                className="bg-f1-icon-positive"
                style={{
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  right: '0',
                  height: '2px',
                  opacity: isColumnSelected ? '0.7' : '0.3',
                  pointerEvents: 'none',
                }}
              />
            ) : null}
          </Box>
        )
      })}
    </Box>
  )
}

export default TimeGridHeader
